import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore, type User } from '../stores/auth.store';
import { selectedWorkspaceParam } from '../stores/workspace.store';
import { deviceClass, userIsActive } from '../utils/user-activity';

/** Every path that starts a session maps the user through here, so the three that
 *  used to do it inline cannot drift apart. */
export function toSessionUser(u: {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: User['role'];
  settings?: User['settings'];
  birthday?: string | null;
  birthdays_today?: User['birthdays_today'];
  workspaces?: Array<{ id: string; name: string }>;
}): User {
  return {
    id: u.id,
    email: u.email,
    name: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email,
    role: u.role,
    workspaces: u.workspaces ?? [],
    settings: u.settings,
    birthday: u.birthday ?? null,
    birthdays_today: u.birthdays_today ?? [],
  };
}

const api = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  // No-op while the API is same-origin (relative baseURL), which it is today —
  // kept so the refresh cookie still travels if the API ever moves origin.
  withCredentials: true,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { accessToken } = useAuthStore.getState();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  if (userIsActive()) config.headers['X-User-Active'] = '1';
  config.headers['X-Device'] = deviceClass();
  // View filter rides on reads only; a write gates on the full access set server-side.
  const method = (config.method ?? 'get').toLowerCase();
  if (method === 'get' || method === 'post') {
    const workspaceIds = selectedWorkspaceParam();
    if (workspaceIds) {
      config.params = { workspaceIds, ...(config.params as Record<string, unknown>) };
    }
  }
  return config;
});

// --- Refresh-token queue ---------------------------------------------------
// Concurrent 401s (and the page-load recovery) share one /auth/refresh instead of
// stampeding it. Overlap would be harmless — the server issues an access token
// only and deliberately does not rotate the refresh cookie.
let refreshPromise: Promise<string> | null = null;

export function doRefresh(): Promise<string> {
  if (!refreshPromise) {
    // Bare axios, not `api`: a 401 here must not re-enter the response interceptor
    // and call this again. isAuthEndpoint guards that too, but a structural
    // guarantee beats a conditional one when the failure mode is an endless loop.
    refreshPromise = axios
      .post('/api/v1/auth/refresh', {}, { withCredentials: true })
      .then((response) => {
        const data = response.data?.data;
        const newAccessToken = data?.accessToken;
        if (!newAccessToken) {
          throw new Error('No access token in refresh response');
        }
        // If refresh returns user info, restore the full session
        if (data.user) {
          useAuthStore.getState().setAuth(toSessionUser(data.user), newAccessToken);
        } else {
          useAuthStore.getState().setAccessToken(newAccessToken);
        }
        return newAccessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

const UNAUTHENTICATED_ENDPOINTS = [
  '/auth/refresh',
  '/auth/login',
  '/auth/logout',
  '/auth/passkey/login/options',
  '/auth/passkey/login/verify',
];

// Response interceptor to handle token refresh and error extraction
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse<unknown>>) => {
    const originalRequest = error.config;
    const url = originalRequest?.url || '';

    // A 401 from one of these is the answer, not an expired session: refreshing it
    // loops, or ends in logout() and a reload that wipes the message the page was
    // about to show. Not every /auth path — /auth/me, /auth/passkeys and passkey
    // enrolment are authenticated calls that should still refresh and retry.
    const isAuthEndpoint = UNAUTHENTICATED_ENDPOINTS.some((p) => url.startsWith(p));

    if (error.response?.status === 401 && originalRequest && !isAuthEndpoint) {
      try {
        const newAccessToken = await doRefresh();
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch {
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
    }

    // The API sends `error` as { code, message, details }; passing the object straight to
    // `new Error` rendered every failure as "[object Object]".
    const apiError = error.response?.data?.error;
    const message = (typeof apiError === 'string' ? apiError : apiError?.message)
        || error.message || 'An error occurred';
    return Promise.reject(new Error(message));
  }
);

export default api;

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string; details?: unknown };
  _meta?: {
    total: number;
  };
}

// Query DSL types
export interface QueryPayload {
  where?: WhereCondition;
  offset?: number;
  limit?: number;
  no_count?: boolean;
  order_by?: string;
  order?: 'asc' | 'desc';
}

export type WhereCondition =
  | { $and: WhereCondition[] }
  | { $or: WhereCondition[] }
  | { $not: WhereCondition }
  | { [entity: string]: { [field: string]: FieldCondition | unknown } };

export type FieldCondition =
  | { op: '=' | '!=' | '>' | '<' | '>=' | '<=' | 'ilike' | 'like'; value: unknown }
  | { op: 'in' | 'not_in'; value: unknown[] }
  | { op: 'between'; value: [unknown, unknown] }
  | { op: 'is_null' | 'is_not_null' };

export interface QueryResponse<T> {
  success: boolean;
  data: T[];
  _meta?: { total: number };
}
